import { useCallback, useEffect, useState } from "react";
import { layoutTourCallout, measureTourTarget } from "./tourMeasure";

/** Measures target + callout/arrow layout; keeps retrying until the DOM is ready. */
export function useTourLayout(
  active,
  selector,
  extraDeps = [],
  { compact = true } = {},
) {
  const [layout, setLayout] = useState(null);

  const remeasure = useCallback(() => {
    if (!selector) {
      setLayout(null);
      return;
    }
    const target = measureTourTarget(selector);
    if (!target) {
      setLayout(null);
      return;
    }
    setLayout(layoutTourCallout(target, { selector, compact }));
  }, [selector, compact]);

  useEffect(() => {
    if (!active || !selector) {
      setLayout(null);
      return undefined;
    }
    remeasure();
    const delays = [0, 60, 140, 280, 480, 800];
    const timers = delays.map((ms) => window.setTimeout(remeasure, ms));
    const el =
      document.querySelector(`${selector}[data-act-active="1"]`) ||
      document.querySelector(selector);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        remeasure();
      }, 120);
    }
    const t = window.setInterval(remeasure, 240);
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(t);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selector, remeasure, ...extraDeps]);

  return layout;
}
